import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import axios from 'axios';
import { auth } from '../firebase';
import { clearScadaCapabilityCache } from '../components/useScadaCapability';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  // Organisation the user belongs to — { id, name, members: [...] } or null
  // while loading / unauthenticated. The /users/me endpoint returns this
  // alongside `user` so a single request hydrates both.
  const [organisation, setOrganisation] = useState(null);
  const [loading, setLoading] = useState(true);
  // True while we're fetching the user's profile from our DB. Distinct from
  // `loading` (which is the initial Firebase auth check). Routes should treat
  // BOTH as "still loading" to avoid a flash of /setup-profile during login.
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileComplete, setProfileComplete] = useState(false);

  // Helper to get auth headers
  const getAuthHeaders = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return {};
    const token = await currentUser.getIdToken();
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch user profile from our DB. If Firebase has the user but our DB
  // doesn't (404), auto-register and retry — this makes the system resilient
  // to drift (e.g. signup completed in Firebase but register call failed).
  const fetchUserProfile = async () => {
    setProfileLoading(true);
    const headers = await getAuthHeaders();
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/me`, { headers });
      const profile = response.data.user;
      setUserProfile(profile);
      setOrganisation(response.data.organisation || null);
      setProfileComplete(profile?.profile_completed || false);
      return profile;
    } catch (err) {
      if (err?.response?.status === 404) {
        try {
          const currentUser = auth.currentUser;
          await axios.post(
            `${API_BASE_URL}/api/auth/register`,
            { firebase_uid: currentUser?.uid, email: currentUser?.email },
            { headers }
          );
          const retry = await axios.get(`${API_BASE_URL}/api/users/me`, { headers });
          const profile = retry.data.user;
          setUserProfile(profile);
          setOrganisation(retry.data.organisation || null);
          setProfileComplete(profile?.profile_completed || false);
          return profile;
        } catch (registerErr) {
          console.warn('Auto-register failed', registerErr);
        }
      }
      setUserProfile(null);
      setOrganisation(null);
      setProfileComplete(false);
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  // Listen to Firebase auth state changes
  useEffect(() => {
    let lastUid = null;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Any change of identity invalidates org-scoped caches held outside React.
      // logout() does not reload the page, so a module-level cache would
      // otherwise survive a sign-out and be served to the next user signing in
      // on the same tab.
      if (firebaseUser?.uid !== lastUid) {
        clearScadaCapabilityCache();
        lastUid = firebaseUser?.uid ?? null;
      }
      // Set profileLoading BEFORE setUser so React never renders with
      // (user=truthy, profileLoading=false, profileComplete=stale) — which
      // would cause a flash of /setup-profile during login.
      if (firebaseUser) setProfileLoading(true);
      setUser(firebaseUser);
      if (firebaseUser) {
        await fetchUserProfile();
      } else {
        setUserProfile(null);
        setProfileComplete(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Login with email and password
  // Returns { user, profileComplete } so caller can navigate accordingly
  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    // fetchUserProfile handles 404 → auto-register → retry internally
    const profile = await fetchUserProfile();
    return { result, profileComplete: profile?.profile_completed || false };
  };

  // Sign up with email and password
  const signup = async (email, password) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    // Register user in our database
    const token = await result.user.getIdToken();
    await axios.post(
      `${API_BASE_URL}/api/auth/register`,
      {
        firebase_uid: result.user.uid,
        email: result.user.email,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return result;
  };

  // Logout
  const logout = async () => {
    await signOut(auth);
    clearScadaCapabilityCache();
    setUser(null);
    setUserProfile(null);
    setOrganisation(null);
    setProfileComplete(false);
  };

  // Update user profile (profile setup or later edits)
  const updateUserProfile = async (profileData) => {
    const headers = await getAuthHeaders();
    const response = await axios.patch(
      `${API_BASE_URL}/api/users/me`,
      profileData,
      { headers }
    );
    const updatedProfile = response.data.user;
    setUserProfile(updatedProfile);
    setProfileComplete(updatedProfile?.profile_completed || false);
    return updatedProfile;
  };

  const value = {
    user,
    userProfile,
    organisation,
    loading,
    profileLoading,
    profileComplete,
    login,
    signup,
    logout,
    updateUserProfile,
    fetchUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
